import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../fire";
import axiosClient from "../axiosClient";

/**
 * Shared, cached source of truth for the signed-in user's role.
 *
 * Why this exists: the role is fetched from `/api/users/get-user-role`, an async
 * round-trip. Fetching it ad-hoc inside each page caused the Admin Dashboard menu
 * row on /account to "pop in" a moment after the page rendered (layout shift), and
 * meant the role was fetched twice (account page + AdminFlowLayout guard).
 *
 * This provider fixes both: it fetches the role once per session, caches it in
 * localStorage keyed by uid, and uses a stale-while-revalidate strategy — repeat
 * visits hydrate the cached role on the first paint (no pop-in) while a background
 * refetch keeps it fresh. Note the role gate is purely cosmetic: real access is
 * enforced by AdminFlowLayout and by the server on every admin endpoint, so
 * rendering optimistically from cache carries no security risk.
 *
 * -----------------------------------------------------------------------------
 * FUTURE REFACTOR — replace this with Firebase custom claims.
 * -----------------------------------------------------------------------------
 * The cleaner long-term architecture is to store the role as a Firebase **custom
 * claim** on the user's ID token instead of in a separate DB-backed endpoint:
 *
 *   - Server (Admin SDK): when a user's role changes, call
 *       admin.auth().setCustomUserClaims(uid, { role: "Admin" })
 *     so the role is embedded in the user's ID token.
 *   - Client: read it straight off the decoded token via
 *       const { claims } = await auth.currentUser.getIdTokenResult();
 *       const role = claims.role;
 *     This resolves from the in-memory/cached token with NO network round-trip,
 *     so this entire context AND the /api/users/get-user-role endpoint could be
 *     deleted.
 *   - Server route guards: verify `decodedToken.role` directly from the verified
 *     token — no per-request DB lookup needed.
 *
 * Trade-off to handle: custom claims only refresh when the ID token refreshes
 * (~hourly). So whenever a role changes you must (a) re-set the claim AND
 * (b) force a token refresh on the client — `auth.currentUser.getIdToken(true)` —
 * for the new role to take effect immediately rather than up to an hour later.
 */

interface UserRoleContextType {
  /** The user's role, or null when unknown (logged out / not yet resolved). */
  role: string | null;
  /**
   * True while the role is still being determined — Firebase auth is restoring,
   * OR the user is logged in with no cached role and a fetch is in flight. Goes
   * false once we have a role or have confirmed there's no signed-in user.
   */
  isInitialLoading: boolean;
  /** Force a re-fetch of the role (e.g. after a role change). */
  refresh: () => void;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(
  undefined,
);

const cacheKey = (uid: string) => `userRole:${uid}`;

const readCachedRole = (uid: string | undefined): string | null => {
  if (!uid) return null;
  try {
    return localStorage.getItem(cacheKey(uid));
  } catch {
    return null;
  }
};

export const UserRoleProvider = ({ children }: { children: ReactNode }) => {
  const [user, authLoading] = useAuthState(auth);
  const uid = user?.uid;

  // Hydrate synchronously from cache so repeat visits show the role on the very
  // first paint instead of popping it in after the request resolves.
  const [role, setRole] = useState<string | null>(() => readCachedRole(uid));
  // Becomes true once a fetch has completed (success or failure) for the current
  // user, so a failed/empty role settles as "not admin" instead of spinning.
  const [roleResolved, setRoleResolved] = useState(false);

  const fetchRole = useCallback(async (currentUid: string) => {
    try {
      const res = await axiosClient.get("/api/users/get-user-role");
      const fetchedRole = res.data as string;
      setRole(fetchedRole);
      try {
        localStorage.setItem(cacheKey(currentUid), fetchedRole);
      } catch {
        // localStorage unavailable — keep the in-memory value.
      }
    } catch (e) {
      // Keep any cached role; the role is cosmetic and access is enforced
      // server-side, so a transient failure shouldn't disrupt the UI.
      console.error("Error getting user role: " + String(e));
    } finally {
      setRoleResolved(true);
    }
  }, []);

  useEffect(() => {
    // Wait for Firebase to finish restoring auth. Until then the role is
    // genuinely unknown, so we leave roleResolved false (isInitialLoading stays
    // true) and keep the skeleton up instead of flashing "no admin row".
    if (authLoading) return;

    if (!uid) {
      // Definitively logged out — there is no role to resolve.
      setRole(null);
      setRoleResolved(true);
      return;
    }

    // Logged in: hydrate from this uid's cache (handles account switches), then
    // revalidate in the background.
    setRole(readCachedRole(uid));
    setRoleResolved(false);
    fetchRole(uid);
  }, [authLoading, uid, fetchRole]);

  const refresh = useCallback(() => {
    if (uid) fetchRole(uid);
  }, [uid, fetchRole]);

  // Unknown until we either have a role or have settled (fetched, or confirmed
  // there's no user). Critically this is true during the auth-restore window too,
  // so the skeleton shows from the first paint rather than popping in afterward.
  const isInitialLoading = role === null && !roleResolved;

  return (
    <UserRoleContext.Provider value={{ role, isInitialLoading, refresh }}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRole = () => {
  const context = useContext(UserRoleContext);
  if (!context) {
    throw new Error("useUserRole must be used within a UserRoleProvider");
  }
  return context;
};
